import { WidgetType } from 'widgets/WidgetType';

import { LoggingService } from '@instinet-analytics/core-lib';
import { WidgetsService } from 'widgets/WidgetsService';

import { PortfolioService } from 'portfolio/PortfolioService';
import StreamDataService from 'streamsState/StreamDataService';

import {
    FilteredPortfolioChanged,
    PortfolioEventService,
} from 'portfolio/PortfolioEventService';
import { WidgetStreamConfig } from 'widgets/streaming/WidgetStreamConfig';
import { WidgetRpcStreamManager } from 'widgets/streaming/widgetStreamManager/WidgetRpcStreamManager';
import RequestProgressService from 'requestProgress/RequestProgressService';
import { WidgetStreamManager } from 'widgets/streaming/widgetStreamManager/WidgetStreamManager';
import { WidgetStreamingStreamManager } from 'widgets/streaming/widgetStreamManager/WidgetStreamingStreamManager';
import IndigoApi from 'communication/IndigoApi';
import { FilteredPortfolioChangedEventType } from 'portfolio/store/actions';
import {
    ActionsDeferredDispatcher,
    ActionsImmediateDispatcher,
} from 'store/ActionsDispatcher';
import { IndigoStore } from 'store/store';
import { using as usingStatement } from 'using-statement';
import { selectActiveWidgetIds } from 'layout/store/selectors';
import { buildLogger, Logger } from '@instinet-analytics/core-lib';
import { WidgetStreamRegistrationManager } from 'widgets/streaming/WidgetStreamRegistrationManager';
import {
    isWidgetCustomManagedStreamConfig,
    nullWidgetCustomStreamManager,
    WidgetCustomStreamManager,
} from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManager';
import {
    isWidgetCustomStreamManagerSupportingRegisteredSymbols,
    nullWidgetCustomStreamManagerSupportingRegisteredSymbols,
} from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManagerSupportingRegisteredSymbols';
import IndigoServiceClient from 'communication/IndigoServiceClient';
import { selectTilesState } from 'widgets/tiles/store/selectors';
import { WidgetMarketDataRequestManager } from 'marketData/WidgetMarketDataRequestManager';
import { DataServices } from 'store/Services';
import { WidgetRequestResponseByUidManagerBase } from 'widgets/streaming/widgetStreamManager/RequestResponse/WidgetRequestResponseByUidManagerBase';

export interface WidgetStreamingServiceOptions {
    indigoApi: IndigoApi;
    loggingService: LoggingService;
    widgetsService: WidgetsService;
    portfolioService: PortfolioService;
    portfolioEventService: PortfolioEventService;
    requestProgressService: RequestProgressService;
    streamDataService: StreamDataService;
    indigoServiceClient: IndigoServiceClient;
    marketDataRequestManager: WidgetMarketDataRequestManager;
    dataServices: DataServices;
}

/*
 * Service that co-ordinates the widgets stream
 * Widgets register themselves with this service via the useWidgetStream hook
 * This service assigns an appropriate WidgetStreamManager for each stream for a widget instance
 * Middleware notifies this service that stuff that may affect streams have changed
 * This service notifies the widget stream managers to check their streams when one of their triggers has changed
 */
export class WidgetStreamingService {
    private streamManagers: WidgetStreamManager<unknown>[] = [];
    private readonly indigoApi: IndigoApi;
    private readonly loggingService: LoggingService;
    private readonly logger: Logger;
    private widgetsService: WidgetsService;
    private readonly portfolioService: PortfolioService;
    private readonly streamDataService: StreamDataService;
    private readonly requestProgressService: RequestProgressService;
    private readonly indigoServiceClient: IndigoServiceClient;
    private readonly marketDataRequestManager: WidgetMarketDataRequestManager;
    private readonly dataServices: DataServices;
    private _store: IndigoStore | undefined;
    private registrationManager = new WidgetStreamRegistrationManager();
    private customManagers: {
        [channelDestination: string]: WidgetCustomStreamManager;
    } = {};

    public constructor(options: WidgetStreamingServiceOptions) {
        this.indigoApi = options.indigoApi;
        this.loggingService = options.loggingService;
        this.logger = buildLogger(
            'WidgetStreamingService',
            this.loggingService
        );
        this.portfolioService = options.portfolioService;
        this.widgetsService = options.widgetsService;
        this.streamDataService = options.streamDataService;
        this.requestProgressService = options.requestProgressService;
        this.indigoServiceClient = options.indigoServiceClient;
        this.marketDataRequestManager = options.marketDataRequestManager;
        this.dataServices = options.dataServices;

        options.portfolioEventService.subscribeFilteredPortfolioChanged(
            this.onFilteredPortfolioChangedAsync.bind(this)
        );
    }

    public get store(): IndigoStore {
        if (!this._store) {
            throw new Error('Store must be set');
        }
        return this._store;
    }

    public set store(value: IndigoStore) {
        this._store = value;
    }

    public register(widgetType: WidgetType, widgetKey: string) {
        const nextRegCount = this.registrationManager.register(
            widgetType,
            widgetKey
        );

        this.logger.debug(
            `Registered ${widgetKey} (${nextRegCount} registrations)`
        );

        if (!this.store) {
            throw new Error('Store must be set');
        }

        const { handled: handledByCustomManager } = this.getCustomManager(
            widgetType
        ).register({
            widgetType,
            widgetKey,
        });
        if (handledByCustomManager) {
            return;
        }

        const streamConfigs = this.widgetsService.getStreamConfigs(widgetType);
        if (streamConfigs.length === 0) {
            this.logger.warn(
                `No configs for ${widgetType}, not registering ${widgetKey}`
            );
            return;
        }

        const activeWidgetIds = selectActiveWidgetIds(this.store.getState());

        for (const streamConfig of streamConfigs) {
            const manager = this.buildManager(
                streamConfig,
                widgetType,
                widgetKey
            );
            this.streamManagers.push(manager);
            manager.checkStream({
                dispatcher: new ActionsImmediateDispatcher(this.store),
                reason: 'Initialisation',
                isActive: activeWidgetIds.indexOf(manager.widgetKey) > -1,
            });
        }
    }

    public dispose(widgetType: WidgetType, widgetKey: string) {
        const remainingRegistrations = this.registrationManager.deRegister(
            widgetType,
            widgetKey
        );

        if (remainingRegistrations > 0) {
            this.logger.debug(
                `Not disposing ${widgetKey}: ${remainingRegistrations} registrations remain`
            );
            return;
        }

        this.logger.debug(`Disposing ${widgetKey}`);

        const { handled: disposedByCustomManager } = this.getCustomManager(
            widgetType
        ).dispose({
            widgetType,
            widgetKey,
        });
        if (disposedByCustomManager) {
            return;
        }

        const existing = this.streamManagers.find(
            (m) => m.widgetType === widgetType && m.widgetKey === widgetKey
        );

        if (!existing) {
            return;
        }

        this.streamManagers.splice(this.streamManagers.indexOf(existing), 1);
        existing.dispose();
    }

    public checkPortfolioSelectionStreams() {
        const activeWidgetIds = selectActiveWidgetIds(this.store.getState());
        usingStatement(
            new ActionsDeferredDispatcher(this.store),
            (dispatcher) => {
                usingStatement(
                    this.streamDataService.beginRequestBatch(),
                    () => {
                        Object.values(this.customManagers).forEach((m) =>
                            m.onPortfolioSelectionChanged(dispatcher)
                        );

                        for (const manager of this.streamManagers) {
                            const { streamConfig } = manager;
                            if (
                                streamConfig.configType ===
                                'PortfolioSelectionDependentWidgetStreamConfig'
                            ) {
                                manager.checkStream({
                                    dispatcher,
                                    reason: 'Portfolio Selection Change',
                                    isActive:
                                        activeWidgetIds.indexOf(
                                            manager.widgetKey
                                        ) > -1,
                                });
                            }
                        }
                        this.checkOrderIdsDependentStreams(
                            dispatcher,
                            'Portfolio Selection Change'
                        );
                    }
                );
            }
        );
    }

    public checkAppSettingsDependentStreams() {
        const activeWidgetIds = selectActiveWidgetIds(this.store.getState());
        usingStatement(
            new ActionsDeferredDispatcher(this.store),
            (dispatcher) => {
                usingStatement(
                    this.streamDataService.beginRequestBatch(),
                    () => {
                        Object.values(this.customManagers).forEach((m) =>
                            m.onAppSettingsChanged(dispatcher)
                        );

                        for (const manager of this.streamManagers.filter(
                            (m) => m.streamConfig.subscriptions?.appSettings
                        )) {
                            manager.checkStream({
                                dispatcher,
                                reason: 'App Settings Change',
                                withoutThrottling: true,
                                isActive:
                                    activeWidgetIds.indexOf(manager.widgetKey) >
                                    -1,
                            });
                        }
                    }
                );
            }
        );
    }

    public checkAllStreams(reason: string) {
        const activeWidgetIds = selectActiveWidgetIds(this.store.getState());
        usingStatement(
            new ActionsDeferredDispatcher(this.store),
            (dispatcher) => {
                usingStatement(
                    this.streamDataService.beginRequestBatch(),
                    () => {
                        Object.values(this.customManagers).forEach((m) =>
                            m.checkStream(reason, dispatcher)
                        );

                        for (const manager of this.streamManagers) {
                            manager.checkStream({
                                dispatcher,
                                reason,
                                withoutThrottling: true,
                                isActive:
                                    activeWidgetIds.indexOf(manager.widgetKey) >
                                    -1,
                            });
                        }
                    }
                );
            }
        );
    }

    public checkWidgetStream(widgetType: WidgetType, widgetKey: string) {
        usingStatement(
            new ActionsDeferredDispatcher(this.store),
            (dispatcher) => {
                this.checkWidgetStreamDispatch(
                    widgetType,
                    widgetKey,
                    dispatcher
                );
            }
        );
    }

    /*
     * Clears a response from a custom managed stream
     */
    public clearResponse(
        widgetType: WidgetType,
        widgetKey: string,
        streamKey: string
    ) {
        this.logger.debug('clearResponse', {
            widgetType,
            widgetKey,
            streamKey,
        });
        const customManager = this.getCustomManager(
            widgetType
        ) as WidgetRequestResponseByUidManagerBase;
        if (customManager?.clearResponse) {
            customManager.clearResponse(streamKey);
        }
    }

    public checkWidgetConfigDependentStreams(
        widgetType: WidgetType,
        widgetKey: string
    ) {
        usingStatement(
            new ActionsDeferredDispatcher(this.store),
            (dispatcher) => {
                this.checkWidgetConfigDependentStreamsDispatch(
                    widgetType,
                    widgetKey,
                    dispatcher
                );
            }
        );
    }
    private checkWidgetStreamDispatch(
        widgetType: WidgetType,
        widgetKey: string,
        dispatcher: ActionsDeferredDispatcher
    ) {
        this.getCustomManager(widgetType).checkStream(
            'checkWidgetStream',
            dispatcher
        );

        for (const manager of this.streamManagers.filter(
            (m) => m.widgetType === widgetType && m.widgetKey === widgetKey
        )) {
            manager.checkStream({
                dispatcher,
                reason: 'checkWidgetStream',
                withoutThrottling: true,
                isActive: true,
            });
        }
    }
    private checkWidgetConfigDependentStreamsDispatch(
        widgetType: WidgetType,
        widgetKey: string,
        dispatcher: ActionsDeferredDispatcher,
        isCapsule = false
    ) {
        this.getCustomManager(widgetType).onWidgetConfigChanged(
            {
                widgetType,
                widgetKey,
            },
            dispatcher
        );

        for (const manager of this.streamManagers.filter(
            (m) =>
                (isCapsule || m.streamConfig.subscriptions?.widgetConfig) &&
                m.widgetType === widgetType &&
                m.widgetKey === widgetKey
        )) {
            manager.checkStream({
                dispatcher,
                reason: 'Widget Config Changed',
                withoutThrottling: true,
                isActive: true,
            });
        }

        if (widgetType === WidgetType.TILES) {
            const capsuleContainerConfig =
                selectTilesState(this.store.getState(), widgetKey)?.config ??
                [];
            this.logger.debug(
                'Checking capsules for capsule container ' + widgetKey,
                capsuleContainerConfig.tiles
            );
            for (const capsuleId of capsuleContainerConfig.tiles) {
                this.checkWidgetConfigDependentStreamsDispatch(
                    capsuleId.widgetType,
                    capsuleId.widgetKey,
                    dispatcher,
                    true
                );
            }
        }
    }

    public checkWidgetComponentStateDependentStreams(
        widgetType: WidgetType,
        widgetKey: string
    ) {
        usingStatement(
            new ActionsDeferredDispatcher(this.store),
            (dispatcher) => {
                this.getCustomManager(widgetType).onWidgetComponentStateChanged(
                    {
                        widgetType,
                        widgetKey,
                    },
                    dispatcher
                );

                for (const manager of this.streamManagers.filter(
                    (m) =>
                        m.streamConfig.subscriptions?.widgetComponentState &&
                        m.widgetType === widgetType &&
                        m.widgetKey === widgetKey
                )) {
                    manager.checkStream({
                        dispatcher,
                        reason: 'Widget Component State Changed',
                        withoutThrottling: true,
                        isActive: true,
                    });
                }
            }
        );
    }

    public checkOrderIdsDependentStreams(
        dispatcher: ActionsDeferredDispatcher = new ActionsDeferredDispatcher(
            this.store
        ),
        reason = 'Order Ids Change'
    ) {
        const activeWidgetIds = selectActiveWidgetIds(this.store.getState());
        usingStatement(dispatcher, (dispatcher) => {
            usingStatement(this.streamDataService.beginRequestBatch(), () => {
                usingStatement(this.loggingService.forceLogging(), () => {
                    this.logger.debug(
                        'Active widget ids = ' + activeWidgetIds.join(',')
                    );
                    Object.values(this.customManagers).forEach((m) =>
                        m.onPortfolioStreamingOrderIdsChanged(dispatcher)
                    );

                    for (const manager of this.streamManagers) {
                        const { streamConfig } = manager;
                        if (
                            streamConfig.configType ===
                            'OrderIdsDependentWidgetStreamConfig'
                        ) {
                            manager.checkStream({
                                dispatcher,
                                reason,
                                isActive:
                                    activeWidgetIds.indexOf(manager.widgetKey) >
                                    -1,
                            });
                        }
                    }
                });
            });
        });
    }

    public invokeRpcRequest(widgetType: WidgetType, widgetKey: string) {
        const manager = this.streamManagers.find(
            (m) => m.widgetType === widgetType && m.widgetKey === widgetKey
        );

        if (!manager) {
            this.logger.warn(
                `invokeRpcRequest: No manager found for ${widgetType}/${widgetKey}`
            );
            return;
        }

        if (manager.streamConfig.streamType !== 'RpcStream') {
            this.logger.warn(
                `invokeRpcRequest: Manager for ${widgetType}/${widgetKey} is not an RPC stream`
            );
            return;
        }

        const rpcManager = manager as WidgetRpcStreamManager<unknown>;
        rpcManager.invokeRequest();
    }

    public getInfo(): Array<unknown> {
        const ret = [];
        for (const manager of this.streamManagers) {
            ret.push(manager.getInfo());
        }
        for (const channelDestination of Object.keys(this.customManagers)) {
            ret.push({
                channelDestination,
                ...this.customManagers[channelDestination].getInfo(),
            });
        }
        return ret;
    }

    public resetState() {
        for (const customManager of Object.values(this.customManagers)) {
            customManager.resetState();
        }
    }

    private onFilteredPortfolioChangedAsync(ev?: FilteredPortfolioChanged) {
        if (!ev) {
            return;
        }
        const activeWidgetIds = selectActiveWidgetIds(this.store.getState());
        usingStatement(
            new ActionsDeferredDispatcher(this.store),
            (dispatcher) => {
                usingStatement(
                    this.streamDataService.beginRequestBatch(),
                    () => {
                        Object.values(this.customManagers).forEach((m) =>
                            m.onFilteredPortfolioChangedAsync(dispatcher)
                        );

                        for (const manager of this.streamManagers) {
                            const { streamConfig } = manager;
                            if (
                                streamConfig.configType ===
                                'FilteredPortfolioDependentWidgetStreamConfig'
                            ) {
                                manager.checkStream({
                                    dispatcher,
                                    reason: 'Filtered Portfolio Change',
                                    withoutThrottling:
                                        ev.changedType ===
                                        FilteredPortfolioChangedEventType.RESET,
                                    isActive:
                                        activeWidgetIds.indexOf(
                                            manager.widgetKey
                                        ) > -1,
                                });
                            }
                        }
                    }
                );
            }
        );
    }

    private buildManager<TPayload, TDataMessageOrResponse>(
        streamConfig: WidgetStreamConfig<TPayload, TDataMessageOrResponse>,
        widgetType: WidgetType,
        widgetKey: string
    ): WidgetStreamManager<TPayload, TDataMessageOrResponse> {
        if (streamConfig.streamType === 'StreamingStream') {
            const managerOptions = {
                loggingService: this.loggingService,
                portfolioService: this.portfolioService,
                streamDataService: this.streamDataService,
                marketDataRequestManager: this.marketDataRequestManager,
                store: this.store!,
                widgetType,
                widgetKey,
                streamConfig,
            };
            return new WidgetStreamingStreamManager(managerOptions);
        }
        if (streamConfig.streamType === 'RpcStream') {
            const managerOptions = {
                indigoApi: this.indigoApi,
                loggingService: this.loggingService,
                portfolioService: this.portfolioService,
                requestProgressService: this.requestProgressService,
                marketDataRequestManager: this.marketDataRequestManager,
                store: this.store!,
                widgetType,
                widgetKey,
                streamConfig,
            };
            return new WidgetRpcStreamManager(managerOptions);
        }

        throw new Error('Invalid stream config');
    }

    private getCustomManager(
        widgetType: WidgetType
    ): WidgetCustomStreamManager {
        const config =
            this.widgetsService.getCustomManagedStreamConfig(widgetType);

        if (!config) {
            return nullWidgetCustomStreamManager;
        }

        const key = isWidgetCustomManagedStreamConfig(config)
            ? 'channel-' + config.channelDestination
            : 'restApi-' + config.apiUid;

        if (!this.customManagers[key]) {
            const commonOptions = {
                loggingService: this.loggingService,
                portfolioService: this.portfolioService,
                streamDataService: this.streamDataService,
                widgetsService: this.widgetsService,
                store: this.store,
                channelDestination: key,
                indigoApi: this.indigoApi,
                requestProgressService: this.requestProgressService,
            };
            if (isWidgetCustomManagedStreamConfig(config)) {
                this.customManagers[key] = config.managerFactory({
                    ...commonOptions,
                    channelDestination: config.channelDestination,
                    marketDataRequestManager: this.marketDataRequestManager,
                    dataServices: this.dataServices,
                });
            } else {
                this.customManagers[key] = config.managerFactory({
                    ...commonOptions,
                    endpointUid: config.apiUid,
                    callApi: config.callApi,
                    handleMessage: config.handleMessage,
                    handleError: config.handleError,
                    clearRequestData: config.clearRequestData,
                    removeRequestData: config.removeRequestData,
                    indigoServiceClient: this.indigoServiceClient,
                    marketDataRequestManager: this.marketDataRequestManager,
                    dataServices: this.dataServices,
                });
            }
        }

        return this.customManagers[key];
    }

    public registerSymbolStreams<TConfig>(
        widgetKey: string,
        widgetType: WidgetType,
        symbols: string[],
        config: TConfig,
        quantities?: number[]
    ) {
        this.getSymbolStreamManager(widgetType).registerSymbolStreams(
            widgetKey,
            widgetType,
            symbols,
            config,
            quantities
        );
    }

    private getSymbolStreamManager(widgetType: WidgetType) {
        const customManager = this.getCustomManager(widgetType);
        this.logger.debug(
            `Found customer manager for widget type ${widgetType}`,
            customManager.getInfo()
        );
        return isWidgetCustomStreamManagerSupportingRegisteredSymbols(
            customManager
        )
            ? customManager
            : nullWidgetCustomStreamManagerSupportingRegisteredSymbols;
    }
}
